import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { addToRecentlyViewed } from '../utils/recentlyViewed';
import { ImageLightbox } from '../components/ImageLightbox';
import { 
  MapPin, 
  Eye, 
  Clock, 
  User, 
  DollarSign,
  ArrowLeft,
  Share2,
  Heart,
  Shield,
  CheckCircle,
  Sparkles,
  Copy,
  Mail,
  X,
  Zap,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Trash2,
  AlertTriangle,
  Package,
  Info,
  Plus,
  Expand,
  Bookmark,
  Flag,
  ChevronRight as BreadcrumbIcon,
  Calendar,
  Navigation,
  MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ReportModal } from '../components/ReportModal';
import { formatDistanceToNow } from 'date-fns';
import { toast } from '../lib/toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Calculate distance between two coordinates
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Clean map styling
const CLEAN_MAP_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#e0e0e0' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#d5d5d5' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#b8d4e3' }] },
  { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cccccc' }, { weight: 1 }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#333333' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', elementType: 'labels', stylers: [{ visibility: 'off' }] }
];

// WTB Mini Map Component - same as inventory view
const WTBMiniMap = ({ buyerLat, buyerLng, buyerLocationName, buyerUsername, userLat, userLng, userLocationName, searchRadiusKm }) => {
  const [expanded, setExpanded] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const expandedMapRef = useRef(null);
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  const distance = userLat && userLng 
    ? calculateDistance(userLat, userLng, buyerLat, buyerLng)
    : null;
  
  // Check if user is within buyer's search radius
  const isWithinRadius = distance !== null && searchRadiusKm ? distance <= searchRadiusKm : null;
  
  const getStaticMapUrl = () => {
    const styles = [
      'style=feature:poi|visibility:off',
      'style=feature:transit|visibility:off',
      'style=feature:road|element:geometry|color:0xe0e0e0',
      'style=feature:road|element:labels|visibility:off',
      'style=feature:road.highway|element:geometry|color:0xd0d0d0',
      'style=feature:water|element:geometry|color:0xb8d4e3',
      'style=feature:landscape|element:geometry|color:0xf5f5f5',
      'style=feature:administrative.locality|element:labels.text.fill|color:0x333333',
      'style=feature:administrative.locality|element:labels.text.stroke|color:0xffffff|weight:3'
    ].join('&');
    
    if (userLat && userLng) {
      const distanceKm = calculateDistance(userLat, userLng, buyerLat, buyerLng);
      const zoom = distanceKm < 2 ? 'zoom=13' : '';
      
      const pathEncoded = `path=color:0x9CA3AF80|weight:2|geodesic:true|${userLat},${userLng}|${buyerLat},${buyerLng}`;
      const userMarker = `markers=color:0x1E3A5F|size:small|${userLat},${userLng}`;
      const buyerMarker = `markers=color:0xC9956C|size:small|${buyerLat},${buyerLng}`;
      return `https://maps.googleapis.com/maps/api/staticmap?size=400x200&scale=2&maptype=roadmap&${zoom}&${styles}&${pathEncoded}&${userMarker}&${buyerMarker}&key=${GOOGLE_MAPS_API_KEY}`;
    } else {
      const buyerMarker = `markers=color:0xC9956C|${buyerLat},${buyerLng}`;
      return `https://maps.googleapis.com/maps/api/staticmap?center=${buyerLat},${buyerLng}&zoom=13&size=400x200&scale=2&maptype=roadmap&${styles}&${buyerMarker}&key=${GOOGLE_MAPS_API_KEY}`;
    }
  };
  
  const initExpandedMap = useCallback(() => {
    if (!expandedMapRef.current || !window.google) return;
    
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: buyerLat, lng: buyerLng });
    if (userLat && userLng) {
      bounds.extend({ lat: userLat, lng: userLng });
    }
    
    const map = new window.google.maps.Map(expandedMapRef.current, {
      center: { lat: buyerLat, lng: buyerLng },
      zoom: 12,
      styles: CLEAN_MAP_STYLES,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
      scaleControl: false,
      rotateControl: false,
      keyboardShortcuts: false,
      clickableIcons: false
    });
    
    if (userLat && userLng) {
      const distanceKm = calculateDistance(userLat, userLng, buyerLat, buyerLng);
      
      // Include search radius in bounds if it exists
      if (searchRadiusKm) {
        // Extend bounds to include the radius circle
        const radiusInDegrees = searchRadiusKm / 111; // Approximate km to degrees
        bounds.extend({ lat: buyerLat + radiusInDegrees, lng: buyerLng });
        bounds.extend({ lat: buyerLat - radiusInDegrees, lng: buyerLng });
        bounds.extend({ lat: buyerLat, lng: buyerLng + radiusInDegrees });
        bounds.extend({ lat: buyerLat, lng: buyerLng - radiusInDegrees });
      }
      
      if (distanceKm < 2 && !searchRadiusKm) {
        map.setCenter({ lat: buyerLat, lng: buyerLng });
        map.setZoom(12);
      } else {
        map.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
        const listener = window.google.maps.event.addListener(map, 'idle', () => {
          if (map.getZoom() > 13) map.setZoom(13);
          window.google.maps.event.removeListener(listener);
        });
      }
    } else {
      // If no user location but we have search radius, zoom to show the radius
      if (searchRadiusKm) {
        const radiusInDegrees = searchRadiusKm / 111;
        const radiusBounds = new window.google.maps.LatLngBounds();
        radiusBounds.extend({ lat: buyerLat + radiusInDegrees, lng: buyerLng + radiusInDegrees });
        radiusBounds.extend({ lat: buyerLat - radiusInDegrees, lng: buyerLng - radiusInDegrees });
        map.fitBounds(radiusBounds, 40);
      } else {
        map.setZoom(12);
      }
    }
    
    // Search radius circle (shows how far buyer is willing to travel)
    if (searchRadiusKm) {
      new window.google.maps.Circle({
        map,
        center: { lat: buyerLat, lng: buyerLng },
        radius: searchRadiusKm * 1000, // Convert km to meters
        fillColor: '#C9956C',
        fillOpacity: 0.1,
        strokeColor: '#C9956C',
        strokeOpacity: 0.4,
        strokeWeight: 2
      });
    }
    
    // Buyer marker (beige)
    new window.google.maps.Marker({
      map,
      position: { lat: buyerLat, lng: buyerLng },
      title: buyerLocationName || 'Buyer Location',
      icon: {
        url: `data:image/svg+xml,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">
            <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 24 12 24s12-16.8 12-24c0-6.6-5.4-12-12-12z" fill="#C9956C"/>
            <circle cx="12" cy="12" r="5" fill="white"/>
          </svg>
        `)}`,
        scaledSize: new window.google.maps.Size(32, 48),
        anchor: new window.google.maps.Point(16, 48)
      }
    });
    
    if (userLat && userLng) {
      // User marker (navy)
      new window.google.maps.Marker({
        map,
        position: { lat: userLat, lng: userLng },
        title: userLocationName || 'Your Location',
        icon: {
          url: `data:image/svg+xml,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">
              <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 24 12 24s12-16.8 12-24c0-6.6-5.4-12-12-12z" fill="#1E3A5F"/>
              <circle cx="12" cy="12" r="5" fill="white"/>
            </svg>
          `)}`,
          scaledSize: new window.google.maps.Size(32, 48),
          anchor: new window.google.maps.Point(16, 48)
        }
      });
      
      // Dotted line
      new window.google.maps.Polyline({
        map,
        path: [
          { lat: userLat, lng: userLng },
          { lat: buyerLat, lng: buyerLng }
        ],
        strokeColor: '#6B7280',
        strokeOpacity: 0,
        strokeWeight: 0,
        icons: [{
          icon: {
            path: 'M 0,-1 0,1',
            strokeOpacity: 0.6,
            strokeColor: '#9CA3AF',
            strokeWeight: 2,
            scale: 3
          },
          offset: '0',
          repeat: '10px'
        }]
      });
    }
  }, [buyerLat, buyerLng, userLat, userLng, buyerLocationName, userLocationName, searchRadiusKm]);
  
  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      return;
    }
    
    const existingScript = document.getElementById('google-maps-script');
    if (existingScript) {
      existingScript.addEventListener('load', () => setMapLoaded(true));
      return;
    }
    
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);
  }, [GOOGLE_MAPS_API_KEY]);
  
  // Initialize map when modal opens
  useEffect(() => {
    if (expanded && mapLoaded) {
      const timer = setTimeout(initExpandedMap, 100);
      return () => clearTimeout(timer);
    }
  }, [expanded, mapLoaded, initExpandedMap]);
  
  return (
    <>
      {/* Mini Map Thumbnail */}
      <div 
        onClick={() => setExpanded(true)}
        className="relative w-full rounded-lg overflow-hidden cursor-pointer border border-[var(--theme-border)] hover:border-[#1E3A5F] transition-colors group"
        data-testid="wtb-mini-map"
      >
        <img 
          src={getStaticMapUrl()} 
          alt={`Map showing ${buyerLocationName}`}
          className="w-full h-28 object-cover"
        />
        
        {/* Distance badge */}
        {distance !== null && (
          <div className="absolute top-2 right-2 bg-[#1E3A5F]/90 backdrop-blur-sm text-white text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
            <Navigation className="w-3 h-3" />
            {distance < 1 ? '<1' : Math.round(distance)} km
          </div>
        )}
        
        {/* Expand hint */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-1.5">
            <Expand className="w-4 h-4 text-white" />
          </div>
        </div>
      </div>
      
      {/* Expanded Map Modal */}
      {expanded && (
        <div 
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setExpanded(false)}
        >
          <div 
            className="bg-[var(--theme-card)] rounded-xl overflow-hidden max-w-2xl w-full shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-[var(--theme-border)] flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-[var(--theme-text-primary)]">Buyer Location</h3>
                <p className="text-sm text-[var(--theme-text-secondary)]">{buyerLocationName}</p>
              </div>
              <button 
                onClick={() => setExpanded(false)}
                className="p-2 hover:bg-[var(--theme-hover)] rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-[var(--theme-text-primary)]" />
              </button>
            </div>
            
            <div 
              ref={expandedMapRef}
              className="w-full h-[400px] bg-[var(--theme-hover)] [&_.gm-style-cc]:!hidden [&_.gmnoprint]:!hidden [&_a[href*='google']]:!hidden [&_a[href*='terms']]:!hidden"
            />
            
            <div className="p-4 border-t border-[var(--theme-border)] flex items-center justify-between">
              {/* Legend */}
              <div className="flex items-center gap-4 text-sm">
                {userLat && userLng && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-[#1E3A5F]" />
                    <span className="text-[var(--theme-text-secondary)]">Your location</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-[#C9956C]" />
                  <span className="text-[var(--theme-text-secondary)]">@{buyerUsername || 'Buyer'}'s location</span>
                </div>
                {searchRadiusKm && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-full border-2 border-[#C9956C] bg-[#C9956C]/20" />
                    <span className="text-[var(--theme-text-secondary)]">@{buyerUsername || 'Buyer'}'s travel radius</span>
                  </div>
                )}
              </div>
              
              {/* Distance - aligned right */}
              {distance !== null && (
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                  isWithinRadius 
                    ? 'bg-[#059669]/10 text-[#059669]' 
                    : 'bg-[var(--theme-hover)] text-[var(--theme-text-primary)]'
                }`}>
                  <Navigation className={`w-4 h-4 ${isWithinRadius ? 'text-[#059669]' : 'text-[#C9956C]'}`} />
                  <span className="font-semibold">
                    ~{distance < 1 ? '<1' : Math.round(distance)} km away
                  </span>
                </div>
              )}
            </div>
            
            {/* Travel radius notice - only show when outside radius */}
            {distance !== null && searchRadiusKm && !isWithinRadius && (
              <div className="mx-4 mb-4 p-3 rounded-lg bg-[#C9956C]/10 border border-[#C9956C]/30">
                <p className="text-sm text-[var(--theme-text-primary)] flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#C9956C] text-white flex items-center justify-center text-xs font-bold mt-0.5">!</span>
                  <span>
                    @{buyerUsername || 'Buyer'} is willing to travel up to {searchRadiusKm} km to make a purchase. This item is {Math.round(distance)} km's away. You may still be able to discuss making arrangements by contacting them.
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const CATEGORY_IMAGES = {
  electronics: 'https://images.unsplash.com/photo-1750797308931-b0d261abb3d5?w=800&h=600&fit=crop',
  furniture: 'https://images.unsplash.com/photo-1673695008224-fa5a9d195dfb?w=800&h=600&fit=crop',
  camera: 'https://images.unsplash.com/photo-1629843041291-d374ccae235a?w=800&h=600&fit=crop',
  clothing: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=800&h=600&fit=crop',
  vehicles: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800&h=600&fit=crop',
  sports: 'https://images.unsplash.com/photo-1461896836934-fffbd4f9b0db?w=800&h=600&fit=crop',
  home: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop',
  collectibles: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop',
  books: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=800&h=600&fit=crop',
  tools: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=600&fit=crop',
  other: 'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?w=800&h=600&fit=crop',
};

// Share Modal Component
const ShareModal = ({ isOpen, onClose, listing, url }) => {
  if (!isOpen) return null;

  const shareText = `Check out this WANTD listing: ${listing.title} ($${listing.min_price}-$${listing.max_price})`;
  
  const handleCopyLink = async () => {
    try {
      // Try the modern clipboard API first
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      // Fallback for when clipboard API is blocked
      try {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Link copied to clipboard!');
      } catch (fallbackErr) {
        // If all else fails, show the URL for manual copying
        toast.info('Copy this link: ' + url);
      }
    }
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`WANTD: Someone is looking for ${listing.title}`);
    const body = encodeURIComponent(`Hey!\n\nI found this listing on WANTD that might interest you:\n\n${listing.title}\nBudget: $${listing.min_price} - $${listing.max_price}\nLocation: ${listing.location_name}\n\nCheck it out here: ${url}\n\nWANTD - Australia's Marketplace`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(`${shareText}\n${url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-[var(--theme-card)] rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-[var(--theme-border)]">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)]">Share This Listing</h2>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-[var(--theme-hover)] rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-[var(--theme-text-secondary)]" />
            </button>
          </div>

          {/* Listing Preview */}
          <div className="p-4 rounded-xl bg-[var(--theme-hover)] border border-[var(--theme-border)] mb-6">
            <p className="text-sm text-[var(--theme-text-secondary)] mb-1">WANTED</p>
            <p className="font-bold text-[var(--theme-text-primary)] mb-2">{listing.title}</p>
            <p className="text-[#059669] font-medium">${listing.min_price} - ${listing.max_price}</p>
          </div>

          {/* Share Options */}
          <div className="space-y-3">
            <Button 
              onClick={handleCopyLink}
              variant="outline"
              className="w-full justify-start rounded-xl border-[var(--theme-border)] h-12"
            >
              <Copy className="w-5 h-5 mr-3 text-[var(--theme-text-primary)]" />
              <span className="text-[var(--theme-text-primary)]">Copy Link</span>
            </Button>

            <Button 
              onClick={handleEmailShare}
              variant="outline"
              className="w-full justify-start rounded-xl border-[var(--theme-border)] h-12"
            >
              <Mail className="w-5 h-5 mr-3 text-[#C9956C]" />
              <span className="text-[var(--theme-text-primary)]">Share via Email</span>
            </Button>

            <Button 
              onClick={handleWhatsAppShare}
              variant="outline"
              className="w-full justify-start rounded-xl border-[var(--theme-border)] h-12"
            >
              <svg className="w-5 h-5 mr-3" fill="#25D366" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span className="text-[var(--theme-text-primary)]">Share via WhatsApp</span>
            </Button>

            {/* Facebook - Coming Soon */}
            <Button 
              variant="outline"
              className="w-full justify-start rounded-xl border-[var(--theme-border)] h-12 opacity-60 cursor-not-allowed"
              disabled
            >
              <svg className="w-5 h-5 mr-3" fill="#1877F2" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className="text-[var(--theme-text-primary)]">Share via Facebook</span>
              <span className="ml-auto text-xs text-[var(--theme-text-muted)] bg-[var(--theme-hover)] px-2 py-0.5 rounded-full">Coming Soon</span>
            </Button>
          </div>

          <p className="text-xs text-[var(--theme-text-muted)] text-center mt-6">
            Know someone who might have this item? Share and help them connect!
          </p>
        </div>
      </div>
    </div>
  );
};

// Potential Matches Component (75-89% matches shown as mini-catalog)
const PotentialMatchesSection = ({ wtbId, token }) => {
  const [potentialMatches, setPotentialMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestingId, setRequestingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPotentialMatches();
  }, [wtbId]);

  const fetchPotentialMatches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/matches/wtb/${wtbId}/potential`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPotentialMatches(response.data.potential_matches || []);
    } catch (error) {
      console.error('Failed to fetch potential matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestMatch = async (inventoryId) => {
    setRequestingId(inventoryId);
    try {
      const response = await axios.post(
        `${API_URL}/api/matches/wtb/${wtbId}/request/${inventoryId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Interest sent! The owner will be notified.');
      // Remove the requested item from potential matches and navigate to the new match
      setPotentialMatches(prev => prev.filter(m => m.inventory_item.id !== inventoryId));
      navigate(`/matches/${response.data.match_id}`);
    } catch (error) {
      console.error('Failed to request match:', error);
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setRequestingId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
        <div className="flex items-center justify-center gap-2 py-8">
          <Loader2 className="w-5 h-5 animate-spin text-[var(--theme-text-muted)]" />
          <span className="text-[var(--theme-text-muted)]">Finding potential matches...</span>
        </div>
      </div>
    );
  }

  if (potentialMatches.length === 0) {
    return null; // Don't show section if no potential matches
  }

  return (
    <div className="p-6 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card" data-testid="potential-matches-section">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-5 h-5 text-[#F59E0B]" />
        <h2 className="font-bold text-[var(--theme-text-primary)]">Potential Matches</h2>
        <span className="ml-auto px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-xs font-semibold text-[#F59E0B]">
          {potentialMatches.length} found
        </span>
      </div>
      
      <p className="text-sm text-[var(--theme-text-secondary)] mb-4">
        These items might be what you&apos;re looking for. Review and let the owner know if you&apos;re interested.
      </p>

      <div className="space-y-3">
        {potentialMatches.map((match) => {
          const item = match.inventory_item;
          const isRequesting = requestingId === item.id;
          
          return (
            <div 
              key={item.id}
              className="p-4 rounded-xl bg-[var(--theme-hover)] border border-[var(--theme-border)] hover:border-[#C9956C]/50 transition-colors"
              data-testid={`potential-match-${item.id}`}
            >
              <div className="flex gap-4">
                {/* Item Image */}
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-[var(--theme-bg)] flex-shrink-0">
                  <img 
                    src={item.images?.[0] || CATEGORY_IMAGES.other}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Item Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-semibold text-[var(--theme-text-primary)] truncate">
                      {item.title}
                    </h3>
                    <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-[#059669]/10 text-xs font-bold text-[#059669]">
                      {Math.round(match.match_score)}%
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3 text-sm text-[var(--theme-text-secondary)] mb-2">
                    <span className="text-[#059669] font-semibold">${item.estimated_value?.toLocaleString()}</span>
                    <span className="capitalize">{item.condition?.replace('_', ' ')}</span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {match.distance_km?.toFixed(1)}km
                    </span>
                  </div>

                  <p className="text-xs text-[var(--theme-text-muted)] mb-2 line-clamp-1">
                    {match.justification?.recommendation}
                  </p>

                  <Button
                    size="sm"
                    className="bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full h-8 px-4"
                    onClick={() => handleRequestMatch(item.id)}
                    disabled={isRequesting}
                    data-testid={`i-want-this-${item.id}`}
                  >
                    {isRequesting ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Heart className="w-3 h-3 mr-2" />
                        I Want This
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function WTBDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, api } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerItems, setOfferItems] = useState([]);
  const [offerLoading, setOfferLoading] = useState(false);
  const [submittingOffer, setSubmittingOffer] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Image carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [id]);

  // Track recently viewed when listing loads (only if not user's own listing)
  useEffect(() => {
    if (listing && user && listing.user_id !== user.id) {
      addToRecentlyViewed(listing, 'wtb');
    }
  }, [listing, user]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wtb/${id}`);
      setListing(response.data);
    } catch (error) {
      console.error('Failed to fetch listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIHaveThis = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    setOfferLoading(true);
    setShowOfferModal(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/matches/wtb/${id}/offer`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'no_inventory') {
        toast.info(response.data.message);
        setShowOfferModal(false);
      } else {
        setOfferItems(response.data.items);
      }
    } catch (error) {
      console.error('Failed to get offer options:', error);
      toast.error(error.response?.data?.detail || 'Failed to load your items');
      setShowOfferModal(false);
    } finally {
      setOfferLoading(false);
    }
  };

  const handleSubmitOffer = async (inventoryId) => {
    setSubmittingOffer(inventoryId);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/matches/wtb/${id}/offer/${inventoryId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Offer sent! The buyer will be notified.');
      setShowOfferModal(false);
      navigate(`/matches/${response.data.match_id}`);
    } catch (error) {
      console.error('Failed to submit offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to send offer');
    } finally {
      setSubmittingOffer(null);
    }
  };

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDeleteListing = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/wtb/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Listing deleted');
      navigate('/dashboard/wanter');
    } catch (error) {
      console.error('Failed to delete listing:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete listing');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-[#E5DDD3] rounded mb-6" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="aspect-[16/9] bg-[#E5DDD3] rounded-2xl" />
                <div className="h-10 w-3/4 bg-[#E5DDD3] rounded" />
                <div className="h-32 bg-[#E5DDD3] rounded-xl" />
              </div>
              <div className="space-y-6">
                <div className="h-64 bg-[#E5DDD3] rounded-xl" />
                <div className="h-40 bg-[#E5DDD3] rounded-xl" />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!listing) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 text-center py-16">
          <div className="w-20 h-20 rounded-full bg-[var(--theme-hover)] flex items-center justify-center mx-auto mb-6">
            <Eye className="w-10 h-10 text-[var(--theme-text-muted)]" />
          </div>
          <h2 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-4">Listing Not Found</h2>
          <p className="text-[var(--theme-text-secondary)] mb-6">This listing may have been removed or doesn&apos;t exist.</p>
          <Button onClick={() => navigate('/')} className="rounded-full bg-[#1E3A5F] hover:bg-[#2D4A6F]">
            Go Home
          </Button>
        </div>
      </Layout>
    );
  }

  const imageUrl = listing.images?.[0] || CATEGORY_IMAGES[listing.category] || CATEGORY_IMAGES.other;
  const timeAgo = formatDistanceToNow(new Date(listing.created_at), { addSuffix: true });
  const flexibilityLabels = ['Firm', 'Slight', 'Moderate', 'Flexible', 'Very Flexible'];
  const shareUrl = window.location.href;
  
  // Category labels for breadcrumbs
  const CATEGORY_LABELS = {
    electronics: 'Electronics',
    furniture: 'Furniture',
    clothing: 'Clothing',
    vehicles: 'Vehicles',
    sports: 'Sports & Outdoors',
    home: 'Home & Garden',
    collectibles: 'Collectibles',
    books: 'Books & Media',
    tools: 'Tools & Equipment',
    camera: 'Camera & Photo',
    other: 'Other'
  };
  const categoryLabel = CATEGORY_LABELS[listing.category] || listing.category;
  
  // Get member since year
  const getMemberYear = () => {
    if (listing.user_created_at) {
      return new Date(listing.user_created_at).getFullYear();
    }
    return null;
  };
  const memberYear = getMemberYear();

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 lg:h-[calc(100vh-80px)] lg:flex lg:flex-col lg:overflow-hidden">
        {/* Breadcrumbs and Actions Row */}
        <div className="flex items-center justify-between py-3 flex-shrink-0">
          {/* Back arrow + Breadcrumbs */}
          <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-full hover:bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] transition-colors flex-shrink-0"
            title="Go back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <nav className="flex items-center gap-2 text-sm">
            <Link
              to="/"
              className="px-3 py-1 rounded-full bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)] hover:text-[var(--theme-text-primary)] transition-colors"
            >
              Home
            </Link>
            <BreadcrumbIcon className="w-3.5 h-3.5 text-[var(--theme-text-muted)]" />
            <Link
              to={`/?tab=wants&category=${listing.category}`}
              className="px-3 py-1 rounded-full bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)] hover:text-[var(--theme-text-primary)] transition-colors"
            >
              {categoryLabel}
            </Link>
            <BreadcrumbIcon className="w-3.5 h-3.5 text-[var(--theme-text-muted)]" />
            <span className="px-3 py-1 rounded-full bg-[#1E3A5F]/10 text-[var(--theme-text-primary)] font-medium truncate max-w-[250px]">
              {listing.title}
            </span>
          </nav>
          </div>
          
          {/* Action Buttons with Views */}
          <div className="flex items-center gap-2">
            {/* Views counter */}
            <span className="flex items-center gap-1.5 text-sm text-[var(--theme-text-muted)] mr-2">
              <Eye className="w-4 h-4" />
              {listing.views || 0}
            </span>
            
            <button
              onClick={() => {
                if (!user) {
                  toast.error('Please sign in to save listings');
                  return;
                }
                toast.success('Listing saved!');
              }}
              className="p-2 rounded-full hover:bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] transition-colors"
              title="Save listing"
              data-testid="save-listing-btn"
            >
              <Bookmark className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast.success('Link copied to clipboard!');
              }}
              className="p-2 rounded-full hover:bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] transition-colors"
              title="Share listing"
              data-testid="share-btn"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                if (!user) {
                  toast.error('Please sign in to report listings');
                  return;
                }
                setShowReportModal(true);
              }}
              className="p-2 rounded-full hover:bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:text-red-500 transition-colors"
              title="Report listing"
              data-testid="report-listing-btn"
            >
              <Flag className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Content - Single column centered if no images, else 2-col grid */}
        <div className={`flex flex-col ${listing.images?.length > 0 ? 'lg:grid lg:grid-cols-2' : ''} gap-6 lg:gap-8 lg:flex-1 lg:min-h-0 ${!listing.images?.length ? 'max-w-2xl mx-auto' : ''}`}>
          
          {/* Item Details - Shows FIRST on mobile, SECOND (right column) on desktop if images exist */}
          <div className={`order-1 ${listing.images?.length > 0 ? 'lg:order-2 lg:sticky lg:top-4 lg:self-start' : ''} space-y-5`}>
            {/* Title and Price */}
            <div>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-3" data-testid="wtb-title">
                {listing.title}
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-3xl font-bold text-[#C9956C]" data-testid="wtb-price">
                  ${listing.min_price?.toLocaleString()} - ${listing.max_price?.toLocaleString()}
                </span>
                <span className="px-3 py-1 rounded-full bg-[#1E3A5F] text-white text-xs font-semibold">
                  WANTED
                </span>
              </div>
            </div>

            {/* Buyer + Description Box */}
            <div className="p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)]">
              {/* Buyer row - compact like item view */}
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[var(--theme-border)]">
                <span className="text-sm text-[var(--theme-text-muted)]">Buyer:</span>
                <div className="w-8 h-8 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white text-sm font-bold">
                  {listing.username?.[0]?.toUpperCase() || 'U'}
                </div>
                <span className="font-medium text-[var(--theme-text-primary)]">
                  @{listing.username || 'User'}
                </span>
                <span className="text-[var(--theme-text-muted)]">|</span>
                <span className="text-sm text-[var(--theme-text-secondary)]">
                  Trust: {listing.user_trust_score || 50}
                </span>
                {memberYear && (
                  <>
                    <span className="text-[var(--theme-text-muted)]">|</span>
                    <span className="text-sm text-[var(--theme-text-secondary)]">
                      Member since {memberYear}
                    </span>
                  </>
                )}
              </div>
              
              {/* Description */}
              <div>
                <h3 className="font-semibold text-[var(--theme-text-primary)] mb-2">What I'm Looking For</h3>
                <p className="text-[var(--theme-text-secondary)] whitespace-pre-wrap leading-relaxed">
                  {listing.description || 'No description provided.'}
                </p>
              </div>
            </div>

            {/* Specifications - moved up under user info */}
            <div className="p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)]">
              <div className="grid grid-cols-2 gap-4">
                {/* Left Column - Details */}
                <div className="space-y-3">
                  {/* Category */}
                  <div>
                    <p className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide mb-0.5">Category</p>
                    <p className="font-semibold text-[var(--theme-text-primary)] capitalize">
                      {categoryLabel}
                    </p>
                  </div>
                  
                  {/* Acceptable Conditions */}
                  <div>
                    <p className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide mb-0.5">Acceptable Conditions</p>
                    <p className="font-medium text-[var(--theme-text-primary)] capitalize">
                      {(() => {
                        const allConditions = ['new', 'like_new', 'excellent', 'good', 'fair', 'poor'];
                        const accepted = listing.condition_accepted || [];
                        if (accepted.length >= allConditions.length || accepted.length === 0) {
                          return 'Any';
                        }
                        return accepted.map(c => c.replace('_', ' ')).join(', ');
                      })()}
                    </p>
                  </div>
                  
                  {/* Listed Date */}
                  <div>
                    <p className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide mb-0.5">Listed</p>
                    <p className="font-medium text-[var(--theme-text-primary)] flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {timeAgo}
                    </p>
                  </div>
                </div>
                
                {/* Right Column - Location with Mini Map */}
                <div>
                  <p className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide mb-1">Location: {listing.location_name || 'Not specified'}</p>
                  {/* Mini Map Component */}
                  {listing.location && (() => {
                    const buyerLat = listing.location?.lat || listing.location?.coordinates?.[1];
                    const buyerLng = listing.location?.lng || listing.location?.coordinates?.[0];
                    const userLat = user?.location?.lat || user?.location?.coordinates?.[1];
                    const userLng = user?.location?.lng || user?.location?.coordinates?.[0];
                    
                    if (!buyerLat || !buyerLng) return null;
                    
                    return (
                      <WTBMiniMap
                        buyerLat={buyerLat}
                        buyerLng={buyerLng}
                        buyerLocationName={listing.location_name}
                        buyerUsername={listing.username}
                        userLat={userLat}
                        userLng={userLng}
                        userLocationName={user?.location_name}
                        searchRadiusKm={listing.search_radius_km}
                      />
                    );
                  })()}
                  {/* Search Radius text */}
                  {listing.search_radius_km && (
                    <div className="mt-2 flex items-center gap-1 text-sm">
                      <span className="text-[var(--theme-text-secondary)]">
                        Willing to travel up to {listing.search_radius_km} km
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              {user ? (
                user.id !== listing.user_id && (user.account_type === 'owner' || user.account_type === 'both') ? (
                  <>
                    <Button 
                      onClick={handleIHaveThis}
                      className="flex-1 bg-[#C9956C] hover:bg-[#B8845B] text-white h-12 rounded-full"
                      data-testid="i-have-this-btn"
                    >
                      <Heart className="w-5 h-5 mr-2" />
                      I Have This Item
                    </Button>
                    <Button 
                      onClick={async () => {
                        try {
                          // Start or get existing conversation without offering an item
                          const response = await api.post('/matches/start-conversation', null, {
                            params: { wtb_id: listing.id }
                          });
                          navigate(`/messages/${response.data.match_id}`);
                        } catch (error) {
                          console.error('Failed to start conversation:', error);
                          toast.error(error.response?.data?.detail || 'Failed to start conversation');
                        }
                      }}
                      variant="outline"
                      className="flex-1 border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)] h-12 rounded-full"
                      data-testid="message-buyer-btn"
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Message Buyer
                    </Button>
                  </>
                ) : user.id === listing.user_id ? (
                  <>
                    <Button 
                      variant="outline" 
                      className="flex-1 rounded-full border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)] h-12"
                      onClick={() => navigate(`/dashboard/wanter`)}
                    >
                      Edit Listing
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1 rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 h-12"
                      onClick={() => setShowDeleteDialog(true)}
                      disabled={deleting}
                      data-testid="delete-listing-btn"
                    >
                      {deleting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      Delete
                    </Button>
                  </>
                ) : null
              ) : (
                <Button 
                  className="flex-1 bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white h-12 rounded-full"
                  onClick={() => navigate('/register')}
                >
                  Sign Up to Respond
                </Button>
              )}
            </div>
          </div>

          {/* Image Gallery - Only shows if listing has images */}
          {listing.images?.length > 0 && (
            <div className="order-2 lg:order-1 space-y-3 lg:overflow-y-auto lg:overflow-x-hidden scrollbar-hide lg:pb-4">
              {/* Main Image */}
              <div 
                className="relative rounded-xl overflow-hidden bg-[var(--theme-card)] border border-[var(--theme-border)] cursor-pointer group aspect-[4/3]"
                onClick={() => setLightboxOpen(true)}
                data-testid="wtb-main-image"
              >
                <img 
                  src={listing.images[0]} 
                  alt={listing.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                
                {/* Expand hint overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-3">
                    <Expand className="w-6 h-6 text-white" />
                  </div>
                </div>
                
                {/* WANTED Badge */}
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 rounded-full bg-[#1E3A5F]/90 backdrop-blur-sm text-xs font-bold uppercase tracking-wider text-white">
                    WANTED
                  </span>
                </div>
                
                {/* Verified Buyer Badge */}
                {listing.is_verified_buyer && (
                  <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-[#059669]/90 backdrop-blur-sm flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-white" />
                    <span className="text-xs font-bold text-white">Verified</span>
                  </div>
                )}
                
                {/* Image counter badge */}
                {listing.images.length > 1 && (
                  <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-black/60 text-white text-xs font-medium">
                    1 / {listing.images.length}
                  </div>
                )}
              </div>
              
              {/* Additional Images */}
              {listing.images.slice(1).map((img, idx) => (
                <div 
                  key={idx}
                  className="relative rounded-xl overflow-hidden bg-[var(--theme-card)] border border-[var(--theme-border)] cursor-pointer group aspect-[4/3]"
                  onClick={() => {
                    setCurrentImageIndex(idx + 1);
                    setLightboxOpen(true);
                  }}
                  data-testid={`image-container-${idx + 1}`}
                >
                  <img 
                    src={img}
                    alt={`${listing.title} - Image ${idx + 2}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  
                  {/* Expand hint overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-3">
                      <Expand className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  {/* Image counter badge */}
                  <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-black/60 text-white text-xs font-medium">
                    {idx + 2} / {listing.images.length}
                  </div>
                </div>
              ))}
              
              {/* Potential Matches Section - Only for listing owner */}
              {user && user.id === listing.user_id && token && (
                <PotentialMatchesSection wtbId={id} token={token} />
              )}
            </div>
          )}
          
          {/* Potential Matches - show outside gallery if no images */}
          {!listing.images?.length && user && user.id === listing.user_id && token && (
            <div className="order-2">
              <PotentialMatchesSection wtbId={id} token={token} />
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[var(--theme-card)] border-[var(--theme-border)]">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <AlertDialogTitle className="text-[var(--theme-text-primary)]">Delete Want Listing?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-[var(--theme-text-secondary)]">
              Are you sure you want to delete <strong className="text-[var(--theme-text-primary)]">&quot;{listing?.title}&quot;</strong>? 
              This action cannot be undone and all potential matches will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel 
              className="rounded-full border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-hover)]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteListing}
              disabled={deleting}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Listing'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        listing={listing}
        url={shareUrl}
      />

      {/* Offer Item Modal */}
      {showOfferModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowOfferModal(false)} />
          
          <div className="relative bg-[var(--theme-card)] rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden border border-[var(--theme-border)] max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-[var(--theme-border)]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-[var(--theme-text-primary)]">Select Item to Offer</h2>
                <button 
                  onClick={() => setShowOfferModal(false)}
                  className="p-2 hover:bg-[var(--theme-hover)] rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-[var(--theme-text-secondary)]" />
                </button>
              </div>
              <p className="text-sm text-[var(--theme-text-secondary)] mt-2">
                Choose which of your items to offer for &quot;{listing.title}&quot;
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {offerLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[var(--theme-text-muted)]" />
                  <span className="ml-2 text-[var(--theme-text-muted)]">Loading your items...</span>
                </div>
              ) : offerItems.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-[var(--theme-text-secondary)] mb-4">No matching items in your listings.</p>
                  <Button onClick={() => { setShowOfferModal(false); navigate('/list/item'); }} className="rounded-full">
                    List an Item
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Note if no high-match items */}
                  {!offerItems.some(item => item.match_score >= 75) && (
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 mb-4">
                      <Info className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-[var(--theme-text-primary)] mb-3">
                          It doesn&apos;t seem like you have what the buyer wants in your items. If this is incorrect, you may want to adjust the description of your item to better match what they&apos;re looking for.
                        </p>
                        <Button 
                          onClick={() => { setShowOfferModal(false); navigate('/list/item'); }} 
                          size="sm"
                          className="bg-[#C9956C] hover:bg-[#B8845B] rounded-full text-sm"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add to Inventory
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {offerItems.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center gap-4 p-4 rounded-xl border border-[var(--theme-border)] hover:border-[#C9956C] transition-colors"
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-[var(--theme-hover)] flex-shrink-0">
                        {item.images?.[0] ? (
                          <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-6 h-6 text-[var(--theme-text-muted)]" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[var(--theme-text-primary)] truncate">{item.title}</h4>
                        <div className="flex items-center gap-2 text-sm text-[var(--theme-text-secondary)]">
                          <span>${item.estimated_value?.toLocaleString()}</span>
                          <span>•</span>
                          <span className="capitalize">{item.condition?.replace('_', ' ')}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs font-medium ${
                            item.match_score >= 90 ? 'text-[#059669]' : 
                            item.match_score >= 75 ? 'text-[#F59E0B]' : 'text-[var(--theme-text-muted)]'
                          }`}>
                            {item.match_score.toFixed(0)}% match
                          </span>
                          {item.distance_km != null && (
                            <span className="text-xs text-[var(--theme-text-muted)]">
                              {item.distance_km.toFixed(1)}km away
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <Button
                        size="sm"
                        className="bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full flex-shrink-0"
                        onClick={() => handleSubmitOffer(item.id)}
                        disabled={submittingOffer === item.id}
                      >
                        {submittingOffer === item.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Offer'
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      <ImageLightbox
        images={listing?.images || []}
        initialIndex={currentImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        alt={listing?.title}
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        itemId={listing?.id}
        itemType="wtb"
        itemTitle={listing?.title}
      />
    </Layout>
  );
}
